
import React, { createContext, useContext, useState, useEffect } from 'react';
import { BRANDS, DEFAULT_BRAND } from '../data/brands';
import { DUFA_DATA } from '../data/constants';
import { useQuery } from '@tanstack/react-query';
import { productService, kartelaService, settingsService } from '../services/api';

// 1. Brand Context
const BrandContext = createContext();

export const BrandProvider = ({ children }) => {
    const [activeBrand, setActiveBrand] = useState(DEFAULT_BRAND);

    const switchBrand = (brandId) => {
        const brand = Object.values(BRANDS).find(b => b.id === brandId);
        if (brand) {
            setActiveBrand(brand);
        }
    };

    return (
        <BrandContext.Provider value={{ activeBrand, switchBrand }}>
            {children}
        </BrandContext.Provider>
    );
};

export const useBrand = () => {
    const context = useContext(BrandContext);
    if (!context) {
        throw new Error('useBrand must be used within a BrandProvider');
    }
    return context;
};

// 2. Color Context (Depends on Brand)
const ColorContext = createContext();

export const ColorProvider = ({ children }) => {
    const { activeBrand } = useBrand();
    const [activeColorIndex, setActiveColorIndex] = useState(0);
    const [isFanDeckOpen, setIsFanDeckOpen] = useState(false);

    // Static data map for fallbacks
    const brandDataMap = {
        'dufa': DUFA_DATA
    };

    // Database Queries
    const { data: products, isLoading: isProductsLoading } = useQuery({ queryKey: ['products'], queryFn: productService.getAll });
    const { data: kartelas, isLoading: isKartelasLoading } = useQuery({ queryKey: ['kartelas'], queryFn: kartelaService.getAll });
    const { data: settings, isLoading: isSettingsLoading } = useQuery({ queryKey: ['settings'], queryFn: settingsService.getAll });

    const isDbLoading = isProductsLoading || isKartelasLoading || isSettingsLoading;

    // Compute active brand data from DB
    const currentData = React.useMemo(() => {
        // Boş array döndürüyoruz ki demo tasarım ekranda (FOUC) olarak görünmesin
        if (!products || !kartelas) {
            return { PRODUCTS: [], FEATURED_PRODUCTS: [], COLORS: [], COLLECTIONS: [] };
        }

        const brandProducts = products.filter(p => p.brandId === activeBrand.id).map(p => ({
            ...p,
            desc: p.description, // Compatibility for older components
            category: p.categories?.[0]?.name || 'Genel', // First category for legacy display
            categoryNames: p.categories?.map(c => c.name) || ['Genel']
        })).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        // Featured products for homepage - sorted by featuredOrder
        const featuredProducts = brandProducts
            .filter(p => p.isFeatured === true)
            .sort((a, b) => (a.featuredOrder || 0) - (b.featuredOrder || 0));

        // Debug log
        console.log('[ColorContext] brandProducts:', brandProducts.length, 'featuredProducts:', featuredProducts.length, featuredProducts.map(p => p.name));

        const brandKartelas = kartelas.filter(k => k.brandId === activeBrand.id).map(k => ({
            ...k,
            // Keep a separate subtitle so title and subtitle can be managed independently.
            subtitle: k.metaTitle || k.description || '',
            description: k.description || k.metaTitle || '', // Compatibility
            image: k.coverImage, // Compatibility
            colors: (k.colors || []).map(c => ({
                ...c,
                code: c.hex || c.code, // Use hex for visual code
                text: c.name
            }))
        })).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        // Flatten colors from all kartelas for the main fan deck
        const brandColors = brandKartelas.reduce((acc, k) => {
            const mappedColors = (k.colors || []).map(c => ({
                ...c,
                code: c.hex, // Compatibility for older components
                text: c.name // Fallback for the teaser text
            }));
            return [...acc, ...mappedColors];
        }, []);

        // Return merged data only from DB
        return {
            PRODUCTS: brandProducts,
            FEATURED_PRODUCTS: featuredProducts.length > 0 ? featuredProducts : brandProducts, // If none featured, show all products
            COLORS: brandColors,
            COLLECTIONS: brandKartelas
        };
    }, [products, kartelas, activeBrand.id]);

    // Automatically set to middle color ONLY on brand change (not on every data reload)
    const prevBrandIdRef = React.useRef(activeBrand.id);
    useEffect(() => {
        if (prevBrandIdRef.current !== activeBrand.id) {
            prevBrandIdRef.current = activeBrand.id;
            if (currentData.COLORS.length > 0) {
                const middleIndex = Math.floor(currentData.COLORS.length / 2);
                setActiveColorIndex(middleIndex);
            }
        }
    }, [activeBrand.id, currentData.COLORS.length]);

    const activeColor = currentData.COLORS[activeColorIndex] || currentData.COLORS[0] || { code: '#FFFFFF', name: 'Yükleniyor...', text: 'Lütfen bekleyin...' };

    const value = {
        activeColorIndex,
        setActiveColorIndex,
        activeColor,
        primaryColor: activeColor.code,
        isFanDeckOpen,
        setIsFanDeckOpen,
        brandPrimaryColor: activeBrand.primaryColor,
        brandData: currentData,
        dbProducts: products || [],
        dbKartelas: kartelas || [],
        settings: settings || {},
        isDbLoading
    };

    return (
        <ColorContext.Provider value={value}>
            {children}
        </ColorContext.Provider>
    );
};

export const useColor = () => {
    const context = useContext(ColorContext);
    if (!context) {
        throw new Error('useColor must be used within a ColorProvider');
    }
    return context;
};

// 3. Theme Context
const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState('light');

    const toggleTheme = () => {
        setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    };

    useEffect(() => {
        document.documentElement.classList.remove('dark', 'light');
        document.documentElement.classList.add(theme);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
